/**
 * TypeScript fallback mesh kernels — registered by {@link ./meshKernelsBootstrap}.
 *
 * Production uses Wasm (`enableWasmMeshKernels`). These exports exist for:
 * - Soft-fail when the Wasm pkg is missing / `CURVA_WASM_STROKES=0`
 * - Direct unit tests of the TS algorithms (`strokeMesh.test.ts`, goldens with TS backend)
 *
 * New production code must call `strokePolylineTriangles` / `networkStrokeTriangles` /
 * `evenOddContourTriangles` from `meshKernels.ts`, not these functions.
 *
 * @internal
 */
import type { StrokeAlign } from "@/document/model/styles";
import type { VectorNetwork } from "@/document/model/vectorNetwork";
import type { Point } from "@/document/operations/pathGeometry";
import {
  networkToStrokeTriangleChunks as tsNetworkToStrokeTriangleChunks,
  networkToStrokeTriangles as tsNetworkToStrokeTriangles,
  strokePolylineToTriangles as tsStrokePolylineToTriangles,
} from "./strokeMesh";
import { triangulateEvenOddContours as tsTriangulateEvenOddContours } from "./pathNodes";

export const tsMeshKernelFallback = {
  strokePolylineToTriangles: tsStrokePolylineToTriangles,
  networkToStrokeTriangles: tsNetworkToStrokeTriangles,
  networkToStrokeTriangleChunks: tsNetworkToStrokeTriangleChunks,
  triangulateEvenOddContours: tsTriangulateEvenOddContours,
} as const;

export type TsMeshKernelFallback = typeof tsMeshKernelFallback;

/** @deprecated Prefer meshKernels wrappers; kept for strokeMesh.test TS parity. */
export function strokePolylineToTriangles(
  points: Array<{ x: number; y: number }>,
  strokeWidth: number,
  closed: boolean,
  align?: StrokeAlign,
): number[] {
  return tsStrokePolylineToTriangles(points, strokeWidth, closed, align);
}

/** @deprecated Prefer meshKernels wrappers; kept for strokeMesh.test TS parity. */
export function networkToStrokeTriangles(
  network: VectorNetwork,
  strokeWidth: number,
  align?: StrokeAlign,
): number[] {
  return tsNetworkToStrokeTriangles(network, strokeWidth, align);
}

/** @deprecated Prefer meshKernels wrappers. */
export function networkToStrokeTriangleChunks(
  network: VectorNetwork,
  strokeWidth: number,
  align?: StrokeAlign,
): number[][] {
  return tsNetworkToStrokeTriangleChunks(network, strokeWidth, align);
}

/** @deprecated Prefer evenOddContourTriangles from meshKernels. */
export function triangulateEvenOddContours(contours: Point[][]): number[] {
  return tsTriangulateEvenOddContours(contours);
}
